import { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import LoadingScreen from "../utilities/LoadingScreen";

const apiDomain = process.env.REACT_APP_API_DOMAIN;

const SponsorInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiDomain}/api/v2/sponsor/list-invoices`);
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error fetching sponsor invoices:", e);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleApprove = async (invoiceId) => {
    const confirm = await Swal.fire({
      title: "Approve Sponsorship?",
      text: "This will activate the sponsorship and map the sponsor to the event in the database.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve!",
    });

    if (confirm.isConfirmed) {
      try {
        Swal.showLoading();
        await axios.post(`${apiDomain}/api/v2/sponsor/approve-invoice?invoiceId=${invoiceId}`);
        Swal.fire("Approved!", "The sponsorship has been activated successfully.", "success");
        fetchInvoices();
      } catch (e) {
        console.error("Error approving invoice:", e);
        Swal.fire("Error", e.response?.data || "Failed to approve sponsorship invoice.", "error");
      }
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="container-fluid" style={{ padding: "30px", background: "#F8F9FE", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 style={{ fontWeight: 800, color: "#1E293B", margin: 0 }}>Sponsorship Invoices</h2>
          <p style={{ color: "#64748B", fontSize: "14px", margin: "5px 0 0 0" }}>
            Review, track, and approve corporate B2B sponsorship invoice requests.
          </p>
        </div>
      </div>

      <div className="card shadow-sm border-0" style={{ borderRadius: "16px", overflow: "hidden" }}>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0" style={{ background: "#FFF" }}>
            <thead style={{ background: "#F1F5F9", color: "#475569", fontWeight: "bold" }}>
              <tr>
                <th style={{ padding: "16px 20px" }}>Invoice ID</th>
                <th>Sponsor ID</th>
                <th>Event ID</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Billing Email</th>
                <th>Status</th>
                <th>Requested At</th>
                <th style={{ padding: "16px 20px", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-5" style={{ color: "#64748B" }}>
                    <div className="mb-3" style={{ fontSize: "24px" }}>📄</div>
                    <strong>No sponsorship invoices found.</strong>
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv.invoice_id}>
                    <td style={{ padding: "16px 20px", fontFamily: "monospace", fontWeight: "bold" }}>
                      {inv.invoice_id.substring(0, 8)}...
                    </td>
                    <td>{inv.sponsor_id}</td>
                    <td>{inv.event_id}</td>
                    <td>{inv.category_id}</td>
                    <td style={{ fontWeight: "bold", color: "#0F172A" }}>
                      ₹{parseFloat(inv.amount || 0).toLocaleString("en-IN")}
                    </td>
                    <td>{inv.email}</td>
                    <td>
                      <span
                        className={`badge ${
                          inv.status === "paid" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"
                        }`}
                        style={{ padding: "6px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "bold" }}
                      >
                        {inv.status === "paid" ? "Paid / Active" : "Pending Payment"}
                      </span>
                    </td>
                    <td style={{ color: "#64748B", fontSize: "13px" }}>
                      {new Date((inv.createdAt || 0) * 1000).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td style={{ padding: "16px 20px", textAlign: "right" }}>
                      {inv.status !== "paid" ? (
                        <button
                          className="btn btn-primary btn-sm"
                          style={{
                            borderRadius: "10px",
                            padding: "6px 14px",
                            fontWeight: "bold",
                            border: "none",
                            background: "#2563EB",
                          }}
                          onClick={() => handleApprove(inv.invoice_id)}
                        >
                          Approve
                        </button>
                      ) : (
                        <span className="text-success" style={{ fontWeight: "bold", fontSize: "14px" }}>
                          ✓ Approved
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SponsorInvoices;
